import { EmptyState, FlexColumn, FlexRow, FullContainer, Loading, useLoading } from '@gorila-shared-ui/components';
import { Cell, Grid } from 'baseui/layout-grid';
import { Pagination } from 'baseui/pagination';
import { useEffect, useState } from 'react';
import { useRecoilValue } from 'recoil';
import { useFeedback } from '../../../../hooks/useFeedback';
import { useStyles } from '../../../../hooks/useStyles';
import { getFleetDriverPhotos } from '../../../../services/fleetService';
import { apiParamsState } from '../../../../storage/app';
import { fleetDriverReloadState } from '../../../../storage/fleetDrivers';
import { FileDisplay } from '../../../../types/app';
import { FleetDriverPhotoDetail } from '../../../../types/fleet';
import FleetDriverPhotoDetailCard from './FleetDriverPhotoDetailCard';
import FleetImagesCarouselFullScreenPreviewModal from './FleetImagesCarouselFullScreenPreviewModal';

type Props = Readonly<{ driverId: string }>;

function FleetDriverPhotos({ driverId }: Props) {
  const { css, classes } = useStyles();
  const { showFailFeedback } = useFeedback();
  const { loading, startLoading, stopLoading } = useLoading(true);
  const [page, setPage] = useState(0);
  const [hasNext, setHasNext] = useState(false);
  const [totalItems, setTotalItems] = useState(0);
  const [detailedPhotos, setDetailedPhotos] = useState<FleetDriverPhotoDetail[]>();
  const [selectedDriver, setSelectedDriver] = useState<FileDisplay>();
  const apiParams = useRecoilValue(apiParamsState);
  const reload = useRecoilValue(fleetDriverReloadState);

  useEffect(() => {
    setPage(0);
  }, [reload, apiParams?.clientId, apiParams?.subClientId]);

  useEffect(() => {
    if (page === 0) {
      setPage(1);
      return;
    }
    const loadPhotos = async () => {
      if (!apiParams) {
        stopLoading();
        return;
      }
      startLoading();
      const {
        error,
        photos: photosResponse,
        total,
        hasNext,
      } = await getFleetDriverPhotos({
        driverId,
        page,
        clientId: apiParams.clientId,
        subClientId: apiParams.subClientId,
      });
      if (photosResponse && !error) {
        setDetailedPhotos(photosResponse);
        setTotalItems(total || 0);
        setHasNext(hasNext || false);
      } else {
        setDetailedPhotos(undefined);
        setTotalItems(0);
        setHasNext(false);
        showFailFeedback(error || 'No se pudo cargar las fotos, inténtalo más tarde.');
      }
      stopLoading();
    };
    loadPhotos();
  }, [driverId, apiParams, page]);

  if (loading) {
    return <Loading />;
  }

  const hasPhotos = !!detailedPhotos?.length;

  return (
    <FullContainer verticalFlex>
      {loading && <Loading />}
      {!loading && (
        <FlexColumn
          classNames={css({
            flex: 1,
            overflow: 'hidden',
          })}
        >
          <FlexColumn
            gap={0}
            classNames={css({ flex: 1, overflow: 'hidden' })}
          >
            {!hasPhotos && <EmptyState title="No existen fotografías para este conductor." />}
            {hasPhotos && (
              <Grid
                gridColumns={12}
                gridGaps={[16]}
                gridGutters={[16]}
                gridMargins={[0]}
                behavior="fluid"
                overrides={{
                  GridWrapper: {
                    style: {
                      width: '100%',
                      margin: 0,
                      overflowX: 'hidden',
                    },
                  },
                }}
              >
                {detailedPhotos.map((detailedPhoto, index) => {
                  return (
                    <Cell
                      span={[12, 6, 4, 3, 2]}
                      key={index}
                    >
                      <FleetDriverPhotoDetailCard
                        detail={detailedPhoto}
                        onImageClick={setSelectedDriver}
                      />
                    </Cell>
                  );
                })}
              </Grid>
            )}
          </FlexColumn>
          {hasNext && (
            <FlexRow classNames={classes.centeredEnd}>
              <Pagination
                labels={{
                  nextButton: 'Siguiente',
                  preposition: 'de',
                  prevButton: 'Anterior',
                }}
                size="mini"
                currentPage={page > 0 ? page : 1}
                numPages={Math.ceil(totalItems / 50)}
                onPageChange={({ nextPage }) => setPage(nextPage)}
              />
            </FlexRow>
          )}
        </FlexColumn>
      )}
      {detailedPhotos && selectedDriver && (
        <FleetImagesCarouselFullScreenPreviewModal
          initial={selectedDriver.key}
          photos={detailedPhotos}
          isOpen
          onClose={() => setSelectedDriver(undefined)}
        />
      )}
    </FullContainer>
  );
}

export default FleetDriverPhotos;
